/**
 * The deploy cinematic: what covers the screen between pressing Deploy and standing on the ground.
 *
 * Owner brief (2026-09-04): "there should be a loading screen after selecting deploy, it's just
 * frozen on some random map for like 30 seconds until the game loads" and then "do the same for
 * when you deploy, another part of the story, preferably with some kind of cinematic/subtitle
 * while the map loads in the background. the cinematic/subtitle will fade into the map with the
 * player on the ground ready to play."
 *
 * So: a painted plate with a slow Ken Burns move, a plum scrim, and a passage of lore typed across
 * the bottom two lines at a time. Three things about the construction are load-bearing:
 *
 * 1. **The move is CSS, on transform and opacity only.** `precompileScene` occupies the main
 *    thread for fifteen to twenty seconds on the Spine. A JS-driven pan would freeze solid for
 *    exactly the wait this screen exists to cover; a compositor animation does not.
 * 2. **The subtitles are not inside the fade.** The plate and its scrim cross-fade to the world;
 *    the subtitles stay up over it and fade on their own clock afterwards, which is what makes
 *    the hand-off read as a cut in a film rather than as a screen being taken away.
 * 3. **Nothing here can fail the deploy.** A plate that 404s, a decode that never lands, a
 *    missing image directory — every one of those degrades to the scrim over plum and the deploy
 *    proceeds. The art is the best case, not the requirement.
 *
 * The typer is exposed on its own (`subtitles`) because the insertion flyby keeps feeding it after
 * the plate has gone: the passage carries on over the dropship's bay rather than stopping dead the
 * moment the world appears.
 */

import { randomLore, subtitleLines, type LorePassage } from './lore.js';
import { gfxStats } from '../render/renderer.js';

/**
 * The inconspicuous loading bar under the subtitles (Chris, 2026-09-05: "make a loading bar under
 * the text but make it inconspicuous"). A hairline, not a widget. It reads the deploy's real
 * phases without any wiring from main.ts: the plate is up (connecting, fetching), the shader
 * precompile is running (`gfxStats.precompiling`), the precompile has finished, the world is
 * revealed. Inside a phase it creeps toward that phase's ceiling on a time constant so it is never
 * frozen and never lies past the next milestone.
 */
const BAR_TICK_MS = 100;
const BAR_PHASE = { opened: 0.32, compiling: 0.9, compiled: 0.98 } as const;
/** Seconds to cover ~63% of the distance to the phase ceiling: slow while connecting, slower
 *  while compiling (the Spine precompile runs 15–25 s on a cold cache). */
const BAR_TAU_S = { opened: 6, compiling: 12, compiled: 1.5 } as const;

/** Where the generated plates live. Missing files are handled, not assumed away. */
const PLATE_DIR = '/assets/gen/plates';

/**
 * Which painting a ground gets. Every map lists more than one so a player who runs the Spine ten
 * times in an evening is not looking at the same rectangle each time; the tutorial gets the yard
 * itself, because the first thing a new player sees should be where they are actually going.
 */
const PLATES: Record<string, readonly string[]> = {
  spine: ['spine', 'channel', 'procession', 'lanternfall'],
  yard: ['yard', 'lanternfall'],
};
const PLATE_FALLBACK: readonly string[] = ['spine', 'lanternfall', 'ferry', 'channel'];

/** Candidate extensions, in preference order. The generator may emit either. */
const PLATE_EXTS = ['webp', 'jpg', 'png'] as const;

/** Two lines at a time, cinema style. */
const LINES_AT_ONCE = 2;
/** Characters per second the subtitles type at. Slow enough to read, fast enough to keep up. */
/** Half of the old 42 (Chris, 2026-09-05: "typing too fast, slow it down 50%"). */
const SUB_CPS = 21;
/** How long a finished pair of lines holds before the next pair replaces it. */
const PAIR_HOLD_MS = 1700;
/** The last line lingers this long over the world before it fades (skippable). */
const LINGER_MS = 1500;
/** Plate + scrim cross-fade. Keep in step with the transitions in index.html. */
const REVEAL_MS = 900;
const SUBS_FADE_MS = 600;

const TICK_MS = 40;

export interface SubtitleHandle {
  /** Start typing `passage` from the top, replacing whatever is on screen. */
  show(passage: LorePassage): void;
  /** Fade the subtitles out and stop typing. Safe to call twice. */
  fadeOut(): void;
  /** True while a passage is still being typed or held. */
  readonly running: boolean;
}

export interface CinematicHandle {
  /**
   * Cover the screen and start the show. Called on the same click as the deploy, before any of
   * the slow work begins.
   *
   * `fallbackUrl` is the client's own last hub-backdrop capture: used when no painted plate for
   * this ground can be loaded, so the screen is never bare even before the paintings exist.
   */
  open(mapId: string, fallbackUrl?: string | null): void;
  /** True while the plate is covering the canvas. */
  readonly covering: boolean;
  /**
   * Hand off to the world. The plate and scrim cross-fade out over ~900 ms; the subtitles stay
   * up, linger, and fade. Resolves once the plate is gone (not once the subtitles are).
   *
   * `keepSubtitles` is what the insertion flyby passes: the passage carries on over the ship
   * instead of being wound up, and the caller owns `subtitles.fadeOut()` from then on.
   */
  reveal(opts?: { keepSubtitles?: boolean }): Promise<void>;
  /** Take everything down at once — a return to the hub, an auth kick, a failed deploy. */
  cancel(): void;
  /** The passage on screen, so the next wait can pick a different one. */
  readonly passageId: string;
  readonly subtitles: SubtitleHandle;
}

function need<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/**
 * Resolve a plate to a URL that actually decodes, trying each extension in turn.
 *
 * Deliberately uses `Image` rather than `fetch`: a HEAD that succeeds still tells you nothing
 * about whether the bytes are a decodable image, and this has to be certain before the plate is
 * faded in — a broken-image glyph on the cold-start screen would be worse than no art at all.
 */
async function resolvePlate(names: readonly string[]): Promise<string | null> {
  for (const name of names) {
    for (const ext of PLATE_EXTS) {
      const url = `${PLATE_DIR}/${name}.${ext}`;
      const ok = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve(img.naturalWidth > 0);
        };
        img.onerror = () => {
          resolve(false);
        };
        img.src = url;
      });
      if (ok) return url;
    }
  }
  return null;
}

class Subtitles implements SubtitleHandle {
  private readonly root: HTMLElement | null;
  private readonly lineEls: (HTMLElement | null)[];
  private lines: string[] = [];
  /** Index of the first line of the pair currently on screen. */
  private pair = 0;
  private typed = 0;
  private holdUntil = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private finished = true;

  constructor(root: HTMLElement | null, lineEls: (HTMLElement | null)[]) {
    this.root = root;
    this.lineEls = lineEls;
  }

  get running(): boolean {
    return !this.finished;
  }

  show(passage: LorePassage): void {
    this.stopTimer();
    // The whole passage as one block, not two sentences at a time (Chris, 2026-09-05: "show the
    // whole story instead of two sentences at a time"). `subtitleLines` is kept for the flyby's
    // pilot lines, which are still captions.
    this.lines = [passage.text];
    this.root?.classList.add('whole');
    this.pair = 0;
    this.typed = 0;
    this.holdUntil = 0;
    this.finished = false;
    this.root?.classList.remove('subs-out');
    this.paint();
    this.timer = setInterval(() => {
      this.tick();
    }, TICK_MS);
  }

  fadeOut(): void {
    this.stopTimer();
    this.finished = true;
    this.root?.classList.add('subs-out');
    setTimeout(() => {
      for (const el of this.lineEls) if (el) el.textContent = '';
    }, SUBS_FADE_MS);
  }

  /** Everything down immediately, no fade — used when the whole cinematic is cancelled. */
  clear(): void {
    this.stopTimer();
    this.finished = true;
    for (const el of this.lineEls) if (el) el.textContent = '';
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Total characters in the pair currently being typed. */
  private get pairLength(): number {
    let n = 0;
    for (let i = this.pair; i < Math.min(this.lines.length, this.pair + LINES_AT_ONCE); i++) {
      n += (this.lines[i] ?? '').length;
    }
    return n;
  }

  private tick(): void {
    const now = performance.now();
    if (this.holdUntil > 0) {
      if (now < this.holdUntil) return;
      this.holdUntil = 0;
      this.pair += LINES_AT_ONCE;
      this.typed = 0;
      if (this.pair >= this.lines.length) {
        // The passage is spent. Hold the last pair rather than clearing or looping: a subtitle
        // track that restarts mid-load reads as a bug, and a blank one reads as a hang.
        this.pair = Math.max(0, this.lines.length - LINES_AT_ONCE);
        this.typed = this.pairLength;
        this.finished = true;
        this.stopTimer();
        this.paint();
        return;
      }
      this.paint();
      return;
    }

    const total = this.pairLength;
    this.typed = Math.min(total, this.typed + (SUB_CPS * TICK_MS) / 1000);
    this.paint();
    if (this.typed >= total) this.holdUntil = now + PAIR_HOLD_MS;
  }

  private paint(): void {
    let left = Math.floor(this.typed);
    for (let i = 0; i < LINES_AT_ONCE; i++) {
      const el = this.lineEls[i];
      if (!el) continue;
      const full = this.lines[this.pair + i] ?? '';
      const take = Math.max(0, Math.min(full.length, left));
      const next = full.slice(0, take);
      if (el.textContent !== next) el.textContent = next;
      left -= full.length;
    }
  }
}

export function mountCinematic(): CinematicHandle {
  const root = need<HTMLDivElement>('cine');
  const plateEl = need<HTMLDivElement>('cine-plate');
  const subsRoot = need<HTMLDivElement>('cine-subs');
  const subtitles = new Subtitles(root, [need('cine-l1'), need('cine-l2')]);
  const barFill = need<HTMLElement>('cine-bar-fill');

  // --- the hairline bar -------------------------------------------------------------------
  let bar = 0;
  let sawCompile = false;
  let barTimer: ReturnType<typeof setInterval> | null = null;
  const drawBar = (): void => {
    if (barFill) barFill.style.transform = `scaleX(${bar.toFixed(3)})`;
  };
  const barTick = (): void => {
    const compiling = gfxStats.precompiling;
    if (compiling) sawCompile = true;
    const phase: keyof typeof BAR_PHASE = compiling
      ? 'compiling'
      : sawCompile
        ? 'compiled'
        : 'opened';
    const ceiling = BAR_PHASE[phase];
    const k = 1 - Math.exp(-(BAR_TICK_MS / 1000) / BAR_TAU_S[phase]);
    // A new phase never steps the bar backwards; it only opens more room ahead.
    bar = Math.max(bar, Math.min(ceiling, bar + (ceiling - bar) * k));
    drawBar();
  };
  const startBar = (): void => {
    bar = 0.04;
    sawCompile = false;
    drawBar();
    root?.classList.remove('bar-done');
    if (barTimer) clearInterval(barTimer);
    barTimer = setInterval(barTick, BAR_TICK_MS);
  };
  const finishBar = (): void => {
    if (barTimer) clearInterval(barTimer);
    barTimer = null;
    bar = 1;
    drawBar();
    root?.classList.add('bar-done');
  };

  let covering = false;
  let passageId = '';
  /** Bumped on every open/cancel so a slow plate resolve from a previous deploy is discarded. */
  let generation = 0;
  let skip: (() => void) | null = null;

  /** Plate and scrim gone, subtitles may still be up. */
  const uncover = (): void => {
    covering = false;
    root?.classList.remove('plated');
    if (plateEl) plateEl.style.removeProperty('--plate');
  };

  /** Everything gone, element out of the way. */
  const hide = (): void => {
    uncover();
    root?.classList.remove('on', 'revealing', 'subs-out');
  };

  return {
    get covering(): boolean {
      return covering;
    },
    get passageId(): string {
      return passageId;
    },
    subtitles,

    open(mapId: string, fallbackUrl?: string | null): void {
      const gen = ++generation;
      covering = true;
      root?.classList.add('on');
      root?.classList.remove('revealing', 'plated', 'subs-out');
      // The scrim over plum is up immediately and is on its own enough to cover the canvas; the
      // painting joins when it has decoded. Nothing waits on the art.
      if (plateEl) plateEl.style.removeProperty('--plate');

      const passage = randomLore(passageId);
      passageId = passage.id;
      subtitles.show(passage);
      startBar();

      void resolvePlate(PLATES[mapId] ?? PLATE_FALLBACK).then((url) => {
        if (gen !== generation || !plateEl) return;
        const chosen =
          url ?? (typeof fallbackUrl === 'string' && fallbackUrl.length > 0 ? fallbackUrl : null);
        if (chosen === null) return;
        plateEl.style.setProperty('--plate', `url("${chosen}")`);
        root?.classList.add('plated');
      });
    },

    reveal(opts): Promise<void> {
      if (!covering) return Promise.resolve();
      finishBar();
      root?.classList.add('revealing');
      const keep = opts?.keepSubtitles === true;
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          // The plate is gone. The element stays in the document as the subtitles' host — it
          // never takes input (`pointer-events: none` in index.html), so the world underneath is
          // live and shootable from the moment the cross-fade ends, not 1.5 s later.
          uncover();
          resolve();
        }, REVEAL_MS);

        if (keep) return;
        // Linger, then fade — or go early if the player says they are ready. The listener is
        // one-shot and is torn down whichever way it ends.
        const finish = (): void => {
          if (skip) {
            window.removeEventListener('pointerdown', skip);
            window.removeEventListener('keydown', skip);
            skip = null;
          }
          subtitles.fadeOut();
          setTimeout(() => {
            hide();
          }, SUBS_FADE_MS + 60);
        };
        const timer = setTimeout(finish, REVEAL_MS + LINGER_MS);
        skip = () => {
          clearTimeout(timer);
          finish();
        };
        window.addEventListener('pointerdown', skip, { passive: true });
        window.addEventListener('keydown', skip);
      });
    },

    cancel(): void {
      finishBar();
      generation++;
      if (skip) {
        window.removeEventListener('pointerdown', skip);
        window.removeEventListener('keydown', skip);
        skip = null;
      }
      subtitles.clear();
      hide();
      root?.classList.remove('subs-out');
    },
  };
}
